import * as z from "zod";

export const AddressTypeEnum = z.enum(["billing", "shipping", "additional"]);

export const CoordinatesSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export const AddressSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  address1: z
    .string()
    .min(1, "Address line 1 is required")
    .max(100, "Address line 1 must be less than 100 characters"),
  address2: z
    .string()
    .max(100, "Address line 2 must be less than 100 characters")
    .optional(),
  city: z
    .string()
    .min(1, "City is required")
    .max(30, "City must be less than 30 characters"),
  state: z
    .string()
    .min(1, "State is required")
    .max(30, "State must be less than 30 characters"),
  postal_code: z
    .string()
    .max(12, "Postal code must be less than 12 characters")
    .default("0000"),
  country: z
    .string()
    .length(2, "Country must be a 2-letter code")
    .default("AU"),
  coordinates: CoordinatesSchema.optional(),
  building_image: z.string().optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const AddressTypeEntrySchema = z.object({
  addressType: AddressTypeEnum,
  label: z.string().max(50).optional(),
  created: z.date().default(() => new Date()),
});

export const createAddressSchema = AddressSchema.omit({
  created: true,
  updated: true,
}).extend({
  id: z.string().uuid().optional(),
  company_addresses: z
    .array(
      z.object({
        company_id: z.string().uuid(),
        address_type: AddressTypeEnum,
        label: z.string().max(50).optional(),
      })
    )
    .optional(),
  user_addresses: z
    .array(
      z.object({
        user_id: z.string().uuid(),
        address_type: AddressTypeEnum,
        label: z.string().max(50).optional(),
        is_primary: z.boolean().default(false),
      })
    )
    .optional(),
});

export const updateAddressSchema = AddressSchema.partial()
  .omit({
    id: true,
    created: true,
    updated: true,
  })
  .extend({
    address2: z.string().nullable().optional(),
    coordinates: z
      .object({
        lat: z.number(),
        lng: z.number(),
      })
      .nullable()
      .optional(),
    building_image: z.string().nullable().optional(),
    company_addresses: z
      .array(
        z.object({
          company_id: z.string().uuid(),
          address_type: AddressTypeEnum,
          label: z.string().max(50).optional(),
        })
      )
      .optional(),
    user_addresses: z
      .array(
        z.object({
          user_id: z.string().uuid(),
          address_type: AddressTypeEnum,
          label: z.string().max(50).optional(),
          is_primary: z.boolean().default(false),
        })
      )
      .optional(),
  });

// New schema for checkout with optional shipping address
export const checkoutAddressSchema = AddressSchema.omit({
  id: true,
  created: true,
  updated: true,
}).extend({
  // Make shipping address fields optional
  name: z.string().optional(),
  address1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
  // Keep billing address fields required
  billingAddress1: z.string().min(1, "Billing address is required"),
  billingAddress2: z.string().optional(),
  billingCity: z.string().min(1, "Billing city is required"),
  billingState: z.string().min(1, "Billing state is required"),
  billingPostalCode: z.string().min(1, "Billing postal code is required"),
  billingCountry: z.string().length(2, "Country must be a 2-letter code"),
  // Additional fields
  company_addresses: z
    .array(
      z.object({
        company_id: z.string().uuid(),
        address_type: AddressTypeEnum,
        label: z.string().max(50).optional(),
      })
    )
    .optional(),
  user_addresses: z
    .array(
      z.object({
        user_id: z.string().uuid(),
        address_type: AddressTypeEnum,
        label: z.string().max(50).optional(),
        is_primary: z.boolean().default(false),
      })
    )
    .optional(),
});
